/**
 * Phase 76 Client SDK
 * ─────────────────────────────────────────────────────────────────────────────
 * Thin, strongly-typed wrapper around the Phase 76 SECURITY DEFINER RPCs and
 * scalar helpers. This module is the ONLY surface the rest of the app should
 * import for new RBAC decisions.
 *
 * Mapped to backend artifacts created by:
 *   • supabase/migrations/20260512_phase76_evolutionary_rbac.sql
 *   • supabase/migrations/20260513_phase76_1_evolutionary_rls.sql
 *
 * RPCs exposed
 * ─────────────
 *   get_my_companies()                          → MyCompany[]
 *   get_my_companies_with_modules()             → MyCompanyWithModules[]
 *   get_my_permissions_for_company(uuid)        → text[]
 *   claim_company_via_invite(uuid)              → uuid
 *
 * Scalar helpers
 * ──────────────
 *   is_super_admin()                            → boolean
 *   is_company_owner_or_co_owner(uuid)          → boolean
 *   company_has_module(uuid, text)              → boolean
 *   has_permission(uuid, text)                  → boolean
 *
 * Notes
 * ─────
 *   • Server is the law: every check is gated by RLS regardless of UI state.
 *   • All RPCs are SECURITY DEFINER + STABLE/PLPGSQL and revoked from PUBLIC,
 *     granted to `authenticated` only — so the SDK never needs elevated keys.
 *   • Errors raised by claim_company_via_invite map to known SQLSTATE codes:
 *       P0001  → auth required / invalid argument
 *       P0002  → invite token not recognized
 */

import { supabase } from '@/lib/supabase';

// ─── Public types ────────────────────────────────────────────────────────────

export type MembershipType = 'owner' | 'co_owner' | 'member';

export interface MyCompany {
  company_id:          string;
  registration_number: string;
  legal_name:          string;
  membership_type:     MembershipType;
}

/**
 * Extension of {@link MyCompany} that carries the company's active subscription
 * module keys, sourced from `company_module_subscriptions_v76`. Used by the
 * auth context to hydrate cross-company module availability in a single
 * round-trip (replaces the legacy per-module `companyHasModule` probe loop).
 */
export interface MyCompanyWithModules extends MyCompany {
  /**
   * Active module subscription keys for the company (e.g. ['cashflow','hr']).
   * Always a string[] — the RPC returns `'{}'::text[]` for companies with no
   * active subscriptions, so this is safe to spread into a Set without guards.
   */
  module_keys: string[];
}

/** Known permission keys seeded by Phase 76 migration. */
export type Phase76Permission =
  | 'cashflow.read'
  | 'cashflow.write'
  | 'hr.employee.read.all'
  | 'hr.employee.read.own'
  | 'hr.employee.write'
  | 'inventory.read'
  | 'inventory.write'
  | 'inventory.agents.manage'
  | 'inventory.clients.manage'
  | 'inventory.orders.read'
  | 'inventory.orders.write';

/**
 * Known module keys seeded by Phase 76 migrations.
 *
 * Note: this union is a *hint* for editor autocomplete only — every consumer
 * accepts `Phase76ModuleKey | string`, so newly-seeded dictionary rows in
 * `module_roles_v76` (e.g. Phase 76.2.H's `'package'`) Just Work at runtime
 * without requiring a client release. Widen this union when adding canonical
 * modules so call-sites get proper IDE suggestions.
 */
export type Phase76ModuleKey = 'cashflow' | 'hr' | 'inventory' | 'package';

export class Phase76SdkError extends Error {
  code?: string;
  details?: unknown;
  constructor(message: string, code?: string, details?: unknown) {
    super(message);
    this.name    = 'Phase76SdkError';
    this.code    = code;
    this.details = details;
  }
}

// ─── Internal helpers ────────────────────────────────────────────────────────

function rpcError(op: string, error: { message?: string; code?: string; details?: unknown }): Phase76SdkError {
  const code = error?.code;
  const msg  = error?.message ?? `Phase 76 SDK: ${op} failed.`;
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.error(`[phase76Client] ${op} error:`, error);
  }
  return new Phase76SdkError(msg, code, error?.details);
}

// ─── RPC: get_my_companies ───────────────────────────────────────────────────

export async function getMyCompanies(): Promise<MyCompany[]> {
  const { data, error } = await supabase.rpc('get_my_companies');
  if (error) throw rpcError('get_my_companies', error);
  if (!Array.isArray(data)) return [];
  return (data as MyCompany[]).map((row) => ({
    company_id:          String(row.company_id),
    registration_number: String(row.registration_number ?? ''),
    legal_name:          String(row.legal_name ?? ''),
    membership_type:     (row.membership_type ?? 'member') as MembershipType,
  }));
}

// ─── RPC: get_my_companies_with_modules ──────────────────────────────────────

/**
 * Returns the caller's companies with the active subscription module keys for
 * each, in a single round-trip. Backed by `public.get_my_companies_with_modules()`
 * (Phase 76.2.E migration) which is SECURITY DEFINER + STABLE and joins
 * `get_my_companies()` against `company_module_subscriptions_v76`.
 *
 * The RPC always returns `module_keys` as a non-NULL text[] (defaulting to
 * `'{}'`), so we can safely coerce missing fields to an empty array here.
 */
export async function getMyCompaniesWithModules(): Promise<MyCompanyWithModules[]> {
  const { data, error } = await supabase.rpc('get_my_companies_with_modules');
  if (error) throw rpcError('get_my_companies_with_modules', error);
  if (!Array.isArray(data)) return [];
  return (data as MyCompanyWithModules[]).map((row) => ({
    company_id:          String(row.company_id),
    registration_number: String(row.registration_number ?? ''),
    legal_name:          String(row.legal_name ?? ''),
    membership_type:     (row.membership_type ?? 'member') as MembershipType,
    module_keys:         Array.isArray(row.module_keys)
      ? row.module_keys.filter((k): k is string => typeof k === 'string')
      : [],
  }));
}

// ─── RPC: get_my_permissions_for_company ─────────────────────────────────────

export async function getMyPermissionsForCompany(companyId: string): Promise<string[]> {
  if (!companyId) return [];
  const { data, error } = await supabase.rpc('get_my_permissions_for_company', {
    p_company_id: companyId,
  });
  if (error) throw rpcError('get_my_permissions_for_company', error);
  if (!Array.isArray(data)) return [];
  // Backend returns text[]; supabase-js may surface it as string[] directly.
  return (data as string[]).filter((x): x is string => typeof x === 'string');
}

// ─── RPC: claim_company_via_invite ───────────────────────────────────────────

export async function claimCompanyViaInvite(inviteToken: string): Promise<string> {
  if (!inviteToken) {
    throw new Phase76SdkError('Invite token is required.', 'P0001');
  }
  const { data, error } = await supabase.rpc('claim_company_via_invite', {
    p_invite_token: inviteToken,
  });
  if (error) throw rpcError('claim_company_via_invite', error);
  if (!data || typeof data !== 'string') {
    throw new Phase76SdkError(
      'Phase 76 SDK: claim_company_via_invite returned an unexpected payload.',
      'PHASE76_BAD_PAYLOAD',
      data,
    );
  }
  return data;
}

// ─── Scalar helpers (server-evaluated) ───────────────────────────────────────

export async function isSuperAdmin(): Promise<boolean> {
  const { data, error } = await supabase.rpc('is_super_admin');
  if (error) throw rpcError('is_super_admin', error);
  return Boolean(data);
}

export async function isCompanyOwnerOrCoOwner(companyId: string): Promise<boolean> {
  if (!companyId) return false;
  const { data, error } = await supabase.rpc('is_company_owner_or_co_owner', {
    p_company_id: companyId,
  });
  if (error) throw rpcError('is_company_owner_or_co_owner', error);
  return Boolean(data);
}

export async function companyHasModule(
  companyId: string,
  moduleKey: Phase76ModuleKey | string,
): Promise<boolean> {
  if (!companyId || !moduleKey) return false;
  const { data, error } = await supabase.rpc('company_has_module', {
    p_company_id: companyId,
    p_module_key: moduleKey,
  });
  if (error) throw rpcError('company_has_module', error);
  return Boolean(data);
}

export async function hasPermission(
  companyId: string,
  permission: Phase76Permission | string,
): Promise<boolean> {
  if (!companyId || !permission) return false;
  const { data, error } = await supabase.rpc('has_permission', {
    p_company_id: companyId,
    p_permission: permission,
  });
  if (error) throw rpcError('has_permission', error);
  return Boolean(data);
}

// ─── Convenience: module-key prefix from a permission key ───────────────────

/**
 * Derive a module key from a permission key (e.g. 'hr.employee.read.own' → 'hr').
 * Used by the auth context to compute `subscribedModulesByCompany` locally.
 */
export function modulePrefixOf(permission: string): string {
  const idx = permission.indexOf('.');
  return idx < 0 ? permission : permission.slice(0, idx);
}

// ─── Bundled export (single import point) ────────────────────────────────────

export const phase76 = {
  // RPCs
  getMyCompanies,
  getMyCompaniesWithModules,
  getMyPermissionsForCompany,
  claimCompanyViaInvite,
  // Scalars
  isSuperAdmin,
  isCompanyOwnerOrCoOwner,
  companyHasModule,
  hasPermission,
  // Utilities
  modulePrefixOf,
};

export default phase76;
